/**
 * Client of **OPTIONAL persistent accounts** (`apps/signaling`, endpoints
 * `/api/accounts/*`) — the cookie-BFF layer over guest-first P2P.
 *
 * Susurro works WITHOUT an account (device identity). An account adds the
 * ability to RECOVER the profile on a new device by trying `handle` +
 * passphrase, with two zero-knowledge invariants:
 *
 * - The server **never sees the passphrase**: a `verifier` (hash) is derived ON THE
 *   CLIENT (PBKDF2 → HKDF) and only that opaque hash travels.
 * - The server **cannot decrypt the profile**: the `blob` is sealed with
 *   AES-256-GCM under a key derived from the passphrase, also on the client.
 *   The relay custodies it verbatim.
 *
 * **cookie-BFF (token outside JS):** register/login issue a session that the
 * server places in an **HttpOnly** cookie (`susurro.session`). This client
 * NEVER reads or stores that token: it uses `credentials: "include"` so the
 * browser attaches the cookie. There is no token in `localStorage` nor in JS.
 */

import { bytesToHex, utf8Decode, utf8Encode } from "./crypto/encoding";
import { hkdfExpand, openBase64, sealBase64 } from "./crypto/primitives";
import type { FetchLike } from "./transport";

/** Account profile: arbitrary JSON the app wants to recover (name, etc.). */
export type AccountProfile = Record<string, unknown>;

/** Account session as seen by the app (only the handle; the token goes in a cookie). */
export interface AccountSession {
  readonly handle: string;
}

/** Result of a login: handle + decrypted profile. */
export interface AccountLogin extends AccountSession {
  readonly profile: AccountProfile;
}

/** PBKDF2 iterations (deliberate cost against offline brute force of the verifier). */
const PBKDF2_ITERATIONS = 210_000;
const MASTER_BYTES = 32;
const VERIFIER_INFO = "susurro:acct:verifier:v1";
const PROFILE_KEY_INFO = "susurro:acct:profile-key:v1";
const PROFILE_AAD = "susurro:acct:profile:v1";

/** Safe view of an external JSON as a record (without `as` over `unknown`). */
function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? { ...value } : {};
}

function ab(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.length);
  new Uint8Array(copy).set(bytes);
  return copy;
}

/**
 * Derives the master material from the passphrase with PBKDF2-SHA256, tied to the
 * `handle` (same passphrase on different handles ⇒ different material).
 */
async function deriveMaster(handle: string, passphrase: string): Promise<Uint8Array> {
  const base = await crypto.subtle.importKey("raw", ab(utf8Encode(passphrase)), "PBKDF2", false, [
    "deriveBits",
  ]);
  const salt = utf8Encode(`susurro:acct:v1:${handle}`);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt: ab(salt), iterations: PBKDF2_ITERATIONS },
    base,
    MASTER_BYTES * 8,
  );
  return new Uint8Array(bits);
}

/** Derives `{ verifier, profileKey }` from an account identity. */
async function deriveCredentials(
  handle: string,
  passphrase: string,
): Promise<{ verifier: string; profileKey: Uint8Array }> {
  const master = await deriveMaster(handle, passphrase);
  const [verifierBytes, profileKey] = await Promise.all([
    hkdfExpand(master, VERIFIER_INFO),
    hkdfExpand(master, PROFILE_KEY_INFO),
  ]);
  return { verifier: bytesToHex(verifierBytes), profileKey };
}

async function sealProfile(profileKey: Uint8Array, profile: AccountProfile): Promise<string> {
  return sealBase64(profileKey, utf8Encode(JSON.stringify(profile)), PROFILE_AAD);
}

async function openProfile(profileKey: Uint8Array, blob: string): Promise<AccountProfile> {
  if (!blob) {
    return {};
  }
  const plaintext = await openBase64(profileKey, blob, PROFILE_AAD);
  return asRecord(JSON.parse(utf8Decode(plaintext)));
}

/** Thrown when the server rejects an account operation. */
export class AccountError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "AccountError";
  }
}

export class AccountClient {
  constructor(
    private readonly baseUrl: string = "",
    private readonly fetchImpl: FetchLike = globalThis.fetch.bind(globalThis),
  ) {}

  private async request(path: string, init: RequestInit): Promise<Response> {
    return this.fetchImpl(`${this.baseUrl}${path}`, {
      ...init,
      // The heart of the cookie-BFF: the browser attaches the HttpOnly session
      // cookie; the token never touches JS.
      credentials: "include",
      headers: { "content-type": "application/json", ...init.headers },
    });
  }

  /**
   * Creates a new account and opens a session. Derives the verifier and seals the profile on
   * the client; the server only receives hash + ciphertext.
   */
  async register(
    handle: string,
    passphrase: string,
    profile: AccountProfile = {},
  ): Promise<AccountSession> {
    const { verifier, profileKey } = await deriveCredentials(handle, passphrase);
    const blob = await sealProfile(profileKey, profile);
    const res = await this.request("/api/accounts/register", {
      method: "POST",
      body: JSON.stringify({ handle, verifier, blob }),
    });
    if (!res.ok) {
      throw new AccountError(res.status, `accounts: register ${res.status}`);
    }
    return { handle };
  }

  /**
   * Verifies credentials and recovers the encrypted profile (it decrypts it on the
   * client). It is the "recovery on a new device".
   */
  async login(handle: string, passphrase: string): Promise<AccountLogin> {
    const { verifier, profileKey } = await deriveCredentials(handle, passphrase);
    const res = await this.request("/api/accounts/login", {
      method: "POST",
      body: JSON.stringify({ handle, verifier }),
    });
    if (!res.ok) {
      throw new AccountError(res.status, `accounts: login ${res.status}`);
    }
    const body = asRecord(await res.json());
    const blob = typeof body.blob === "string" ? body.blob : "";
    return { handle, profile: await openProfile(profileKey, blob) };
  }

  /** Closes the session (deletes the cookie server-side). Idempotent. */
  async logout(): Promise<void> {
    await this.request("/api/accounts/logout", { method: "POST", body: "{}" });
  }

  /**
   * Returns the current session by reading the cookie (via the server), or `undefined`
   * if there is no active account (guest). Does not expose the token.
   */
  async me(): Promise<AccountSession | undefined> {
    const res = await this.request("/api/accounts/me", { method: "GET" });
    if (res.status === 401) {
      return undefined;
    }
    if (!res.ok) {
      throw new AccountError(res.status, `accounts: me ${res.status}`);
    }
    const body = asRecord(await res.json());
    return typeof body.handle === "string" ? { handle: body.handle } : undefined;
  }
}
